import {
  Centrifuge,
  SubscriptionState,
  UnauthorizedError,
  unsubscribedCodes,
} from "centrifuge";
import type { Subscription } from "centrifuge";
import apiClient from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { emitRtcDiagnostic } from "@/features/rtc/diagnosticsBus";
import {
  startTransportDiagnostics,
  stopTransportDiagnostics,
} from "@/features/rtc/transportDiagnostics";
import type {
  ConnectionTokenResponse,
  SubscriptionTokenResponse,
} from "../types";

let client: Centrifuge | null = null;
let connectingPromise: Promise<Centrifuge> | null = null;

async function fetchConnectionToken(): Promise<ConnectionTokenResponse> {
  const r = await apiClient.get<{ data: ConnectionTokenResponse }>(
    "/realtime/connection-token/",
  );
  return r.data.data;
}

async function fetchSubscriptionToken(
  channel: string,
): Promise<SubscriptionTokenResponse> {
  const r = await apiClient.get<{ data: SubscriptionTokenResponse }>(
    `/realtime/subscription-token/?channel=${encodeURIComponent(channel)}`,
  );
  return r.data.data;
}

function isLoggedOut(): boolean {
  return !useAuthStore.getState().refreshToken;
}

/** Retry delay for an ordinary transient failure (backend blip, refresh race). */
const RETRY_DELAY_MS = 500;
/** Never park a token fetch longer than this, however long the server asks. */
const MAX_THROTTLE_WAIT_MS = 60_000;

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

/**
 * How long the server told us to wait, in ms — `null` when it did not say.
 *
 * DRF sets `Retry-After` on every throttled response, so the usual answer is a
 * real number; the null branch covers a proxy stripping the header.
 */
function retryAfterMs(err: unknown): number | null {
  const headers = (err as { response?: { headers?: Record<string, unknown> } })
    ?.response?.headers;
  const raw = headers?.["retry-after"] ?? headers?.["Retry-After"];
  const seconds = Number(raw);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return Math.min(seconds * 1000, MAX_THROTTLE_WAIT_MS);
}

/**
 * Wraps a token fetch for centrifuge-js `getToken` callbacks.
 *
 * centrifuge-js 5.x semantics the guards below exist for:
 *  - a FALSY return value (undefined token from an envelope hiccup) is treated
 *    as "unauthorized" and permanently kills the connection/subscription with
 *    no retry — so an empty token must become a thrown error instead;
 *  - a thrown `UnauthorizedError` stops cleanly (right when the user is
 *    genuinely logged out);
 *  - any other thrown error is retried by the SDK with its own backoff — the
 *    right outcome for transient failures (backend blip, the cross-tab JWT
 *    refresh lock in api/client.ts making the endpoint 401 for up to 15 s).
 *    One in-code retry after 500 ms smooths the common short race.
 *
 * A 429 is the exception: it is NOT transient. The rate limit is per USER and
 * shared by every tab and every channel, so retrying 500 ms later spends the
 * very quota we are waiting on and keeps the bucket empty — that feedback loop
 * is what froze the chat on 30.07 (1255 refusals from one browser in 30 min).
 * We wait exactly as long as the server asks, and when it does not say, we hand
 * back to the SDK's own (growing) backoff instead of guessing.
 */
async function guardedToken(
  label: string,
  fetchToken: () => Promise<string | undefined>,
): Promise<string> {
  const attempt = async (): Promise<string> => {
    const token = await fetchToken();
    if (!token) throw new Error(`empty ${label} token`);
    return token;
  };
  try {
    const token = await attempt();
    emitRtcDiagnostic("token_refresh", { label, outcome: "ok" });
    return token;
  } catch (firstError) {
    if (isLoggedOut()) {
      emitRtcDiagnostic("token_refresh", { label, outcome: "logged_out" });
      throw new UnauthorizedError("logged out");
    }
    if (httpStatus(firstError) === 403) {
      // The server DECIDED this user may not have this channel (can_subscribe).
      // That answer does not change in 500 ms, so a retry only mints another
      // refusal: a plain throw hands the SDK its own backoff, which then asks
      // forever (~6 requests/min per denied channel per tab) — the flood of 403s
      // seen on production. UnauthorizedError stops the subscription cleanly;
      // it is re-attempted once on the next transport reconnect / remount.
      emitRtcDiagnostic("token_refresh", {
        label,
        outcome: "forbidden",
        error: String(firstError),
      });
      throw new UnauthorizedError(`forbidden channel (${label})`);
    }
    const throttled = httpStatus(firstError) === 429;
    const waitMs = throttled ? retryAfterMs(firstError) : RETRY_DELAY_MS;
    if (waitMs === null) {
      // Throttled with no Retry-After — retrying now would only deepen it.
      emitRtcDiagnostic("token_refresh", {
        label,
        outcome: "throttled",
        error: String(firstError),
      });
      throw firstError;
    }
    emitRtcDiagnostic("token_refresh", {
      label,
      outcome: throttled ? "throttled_waiting" : "retrying",
      waitMs,
      error: String(firstError),
    });
    await new Promise((resolve) => setTimeout(resolve, waitMs));
    try {
      const token = await attempt();
      emitRtcDiagnostic("token_refresh", { label, outcome: "ok_after_retry" });
      return token;
    } catch (secondError) {
      if (isLoggedOut()) {
        emitRtcDiagnostic("token_refresh", { label, outcome: "logged_out" });
        throw new UnauthorizedError("logged out");
      }
      emitRtcDiagnostic("token_refresh", {
        label,
        outcome: "failed",
        error: String(secondError),
      });
      throw secondError;
    }
  }
}

export async function getRealtimeClient(): Promise<Centrifuge> {
  if (client) return client;
  if (connectingPromise) return connectingPromise;

  connectingPromise = (async () => {
    const initial = await fetchConnectionToken();
    const c = new Centrifuge(initial.url, {
      token: initial.token,
      getToken: () =>
        guardedToken("connection", async () => (await fetchConnectionToken()).token),
    });
    c.on("connected", () => emitRtcDiagnostic("centrifugo_connected"));
    c.on("connecting", (ctx) =>
      emitRtcDiagnostic("centrifugo_connecting", {
        code: ctx.code,
        reason: ctx.reason,
      }),
    );
    c.on("disconnected", (ctx) =>
      emitRtcDiagnostic("centrifugo_disconnected", {
        code: ctx.code,
        reason: ctx.reason,
      }),
    );
    c.on("error", (ctx) =>
      emitRtcDiagnostic("centrifugo_error", {
        type: ctx.type,
        message: ctx.error?.message,
      }),
    );
    // Start the always-on transport diagnostics sink BEFORE the client emits
    // its first connecting/connected event, so the whole reconnect lifecycle is
    // captured for the dev dashboard's GG chart (see transportDiagnostics.ts).
    startTransportDiagnostics();
    c.connect();
    client = c;
    return c;
  })();

  try {
    return await connectingPromise;
  } finally {
    connectingPromise = null;
  }
}

export function getSubscriptionTokenFor(channel: string) {
  return () =>
    guardedToken(
      `subscription:${channel}`,
      async () => (await fetchSubscriptionToken(channel)).token,
    );
}

/**
 * Runs `listener` every time the realtime transport (re-)connects, including
 * the first connect. Used to re-sync side state that silently drifts while the
 * device is away or the socket is down — notably the Web Push subscription,
 * which browsers rotate/expire over time. Returns a cleanup that detaches.
 *
 * The client is created lazily, so we attach as soon as it resolves and guard
 * against the consumer cleaning up before that happens. A `connected` listener
 * left on a client that is later torn down by `disconnectRealtime()` is dropped
 * with the client, so there is nothing to leak across login sessions.
 */
export function onRealtimeConnected(listener: () => void): () => void {
  let cancelled = false;
  let attached: Centrifuge | null = null;
  void getRealtimeClient()
    .then((c) => {
      if (cancelled) return;
      c.on("connected", listener);
      attached = c;
    })
    .catch(() => {
      // Not authenticated / token endpoint down — nothing to attach to.
    });
  return () => {
    cancelled = true;
    try {
      attached?.off("connected", listener);
    } catch {
      // ignore detach errors during teardown
    }
  };
}

interface ManagedSubscription {
  refCount: number;
  sub: Subscription;
  onConnected: () => void;
  onSubscribed: (ctx: { wasRecovering?: boolean; recovered?: boolean }) => void;
  onUnsubscribed: (ctx: { code?: number; reason?: string }) => void;
  client: Centrifuge;
  /** Set right before an intentional `unsubscribe()` so the auto-resubscribe
   *  logic can tell a deliberate release apart from the server/SDK dropping
   *  the channel (e.g. `unsubscribed(unauthorized)` during a JWT refresh
   *  race while the transport stays connected). */
  expectedUnsubscribe: boolean;
  retryTimer: ReturnType<typeof setTimeout> | null;
  retryAttempts: number;
}

const managedSubs = new Map<string, ManagedSubscription>();

const RESUBSCRIBE_DELAYS_MS = [1000, 2000, 5000, 5000, 5000];
// After the fast ladder, keep trying at a slow steady pace instead of giving
// up: a permanently dead subscription is silent (established P2P audio keeps
// flowing, the room LOOKS fine) but no new joiner/offer/ICE-restart ever
// arrives again. ~2 requests/min is a fair price for self-healing.
const RESUBSCRIBE_SLOW_DELAY_MS = 30_000;

/** Centrifugo's own "you may not have this channel" code (103), next to the
 *  SDK's `unauthorized` (raised when `getToken` throws UnauthorizedError). */
const SERVER_PERMISSION_DENIED_CODE = 103;

function isUnauthorizedUnsubscribe(code?: number): boolean {
  return (
    code === unsubscribedCodes.unauthorized ||
    code === SERVER_PERMISSION_DENIED_CODE
  );
}

function scheduleResubscribe(channel: string, entry: ManagedSubscription): void {
  if (entry.retryTimer) return;
  let delay: number;
  if (entry.retryAttempts < RESUBSCRIBE_DELAYS_MS.length) {
    delay = RESUBSCRIBE_DELAYS_MS[entry.retryAttempts];
  } else {
    delay = RESUBSCRIBE_SLOW_DELAY_MS;
    if (entry.retryAttempts === RESUBSCRIBE_DELAYS_MS.length) {
      emitRtcDiagnostic("centrifugo_resubscribe_slow_mode", { channel });
    }
  }
  entry.retryAttempts += 1;
  entry.retryTimer = setTimeout(() => {
    entry.retryTimer = null;
    if (managedSubs.get(channel) !== entry || entry.refCount <= 0) return;
    if (entry.sub.state === SubscriptionState.Unsubscribed) {
      emitRtcDiagnostic("centrifugo_resubscribe_attempt", {
        channel,
        attempt: entry.retryAttempts,
      });
      entry.sub.subscribe();
    }
  }, delay);
}

export interface SubscriptionHandle {
  sub: Subscription;
  release: () => void;
}

/**
 * Acquire a refcounted subscription to a channel. Multiple consumers (the
 * open-room hook and the global room-list hook) share one Centrifugo
 * subscription; the channel is unsubscribed only when the LAST consumer
 * releases it — a plain `sub.unsubscribe()` in either hook's cleanup would
 * otherwise tear the channel down under the other consumer's feet.
 *
 * Callers attach their own "publication"/"error" listeners on `sub` and must
 * remove them before calling `release()`.
 */
export async function acquireSubscription(
  channel: string,
): Promise<SubscriptionHandle> {
  const c = await getRealtimeClient();
  let entry = managedSubs.get(channel);
  if (!entry) {
    const sub =
      c.getSubscription(channel) ??
      c.newSubscription(channel, {
        getToken: getSubscriptionTokenFor(channel),
      });
    // Re-subscribe after every (re-)connection. centrifuge-js v5 does not
    // automatically resubscribe a channel that ended in "unsubscribed" state,
    // so we drive it explicitly each time the transport comes back up. Guard
    // with state check to avoid disrupting the automatic resubscription
    // centrifuge-js performs for channels that were in "subscribed" state
    // before the disconnect.
    const onConnected = () => {
      if (sub.state === SubscriptionState.Unsubscribed) {
        sub.subscribe();
      }
    };
    const newEntry: ManagedSubscription = {
      refCount: 0,
      sub,
      onConnected,
      onSubscribed: () => {},
      onUnsubscribed: () => {},
      client: c,
      expectedUnsubscribe: false,
      retryTimer: null,
      retryAttempts: 0,
    };
    newEntry.onSubscribed = (ctx) => {
      newEntry.retryAttempts = 0;
      emitRtcDiagnostic("centrifugo_subscribed", {
        channel,
        wasRecovering: ctx.wasRecovering,
        recovered: ctx.recovered,
      });
    };
    // The `connected`-only handler above misses channels the server kicks
    // while the transport stays up. Retry those with a capped backoff.
    newEntry.onUnsubscribed = (ctx) => {
      emitRtcDiagnostic("centrifugo_unsubscribed", {
        channel,
        code: ctx.code,
        reason: ctx.reason,
        expected: newEntry.expectedUnsubscribe,
      });
      if (newEntry.expectedUnsubscribe) return;
      if (newEntry.refCount <= 0) return;
      if (managedSubs.get(channel) !== newEntry) return;
      // "Unauthorized" is a verdict, not a blip: the token endpoint said 403
      // (or Centrifugo refused the channel outright). Retrying it just replays
      // the same refusal on a timer — the channel stays dead until the next
      // reconnect (`onConnected`) or a remount, which is exactly when the
      // verdict could have changed.
      if (isUnauthorizedUnsubscribe(ctx.code)) {
        emitRtcDiagnostic("centrifugo_subscription_forbidden", {
          channel,
          code: ctx.code,
          reason: ctx.reason,
        });
        return;
      }
      scheduleResubscribe(channel, newEntry);
    };
    c.on("connected", onConnected);
    sub.on("subscribed", newEntry.onSubscribed);
    sub.on("unsubscribed", newEntry.onUnsubscribed);
    entry = newEntry;
    managedSubs.set(channel, entry);
    sub.subscribe();
  }
  entry.refCount += 1;
  const e = entry;
  let released = false;
  return {
    sub: e.sub,
    release: () => {
      if (released) return;
      released = true;
      e.refCount -= 1;
      if (e.refCount <= 0) {
        // Guard against deleting a NEWER entry for the same channel that may
        // have been created after disconnectRealtime() cleared the registry.
        if (managedSubs.get(channel) === e) managedSubs.delete(channel);
        if (e.retryTimer) {
          clearTimeout(e.retryTimer);
          e.retryTimer = null;
        }
        try {
          e.client.off("connected", e.onConnected);
          e.sub.off("subscribed", e.onSubscribed);
          e.sub.off("unsubscribed", e.onUnsubscribed);
          e.expectedUnsubscribe = true;
          e.sub.unsubscribe();
        } catch {
          // ignore teardown errors
        }
      }
    },
  };
}

export function disconnectRealtime(): void {
  for (const entry of managedSubs.values()) {
    if (entry.retryTimer) {
      clearTimeout(entry.retryTimer);
      entry.retryTimer = null;
    }
    entry.expectedUnsubscribe = true;
    try {
      entry.client.off("connected", entry.onConnected);
      entry.sub.off("subscribed", entry.onSubscribed);
      entry.sub.off("unsubscribed", entry.onUnsubscribed);
    } catch {
      // ignore
    }
  }
  managedSubs.clear();
  try {
    client?.disconnect();
  } catch {
    // Ignore disconnect errors during teardown.
  }
  client = null;
  connectingPromise = null;
  stopTransportDiagnostics();
}
